import { matchPath } from 'react-router-dom';

/**
 * Configuration for routes that should have scrollable content
 *
 * This allows centralized control over which pages/routes should allow
 * content overflow and scrolling vs. using overflow-hidden layouts.
 */

/**
 * Route patterns that should allow content to scroll.
 *
 * These are React Router path patterns, matched with `matchPath` — the same
 * matcher the route tree uses. So '/ui/my-workforce' also covers the
 * '/ui/my-workforce/' spelling, and a trailing '/*' covers the base route plus
 * anything nested under it ('/ui/reports/*' matches '/ui/reports' and
 * '/ui/reports/:insightId', but not '/ui/reportsx').
 */
export const SCROLLABLE_ROUTES = [
  // Main pages
  // NOTE: '/ui/skills' is intentionally absent. Like the Knowledge Hub, the
  // Skills page is a two-pane layout (fixed left pane + routed right viewer)
  // that manages its own internal scroll, so the layout must clip rather than
  // scroll the page.
  '/ui/chats',
  '/ui/routines/*', // /ui/routines and /ui/routines/:routineId
  '/ui/my-workforce',
  '/ui/marketplace',
  '/ui/experiments',
  // NOTE: '/ui/settings' is intentionally absent. Like Skills and the Knowledge
  // Hub, Settings is a two-pane layout whose right pane owns its own scroll, so
  // the shell must clip rather than add a second scroll container.
  '/ui/onboarding/setup',

  '/ui/reports/*', // /ui/reports and /ui/reports/:insightId

  // Demo/utility pages
  '/ui/charts',
  '/ui/slots',
  '/ui/insights',
  '/ui/block-kit-playground',

  // Demo agents (all routes between the demo comments in App.tsx)
  '/ui/agent/bant-qualifier-avoma',
  '/ui/agent/outbound-call-ai-agent',
  '/ui/agent/keyword-optimizer',
  '/ui/agent/lead-funnel-analyser-demo',
  '/ui/agent/linkedin-campaign-analyser-demo',
  '/ui/agent/google-ads-campaign-analyser-demo',
  '/ui/agent/recki',
  '/ui/agent/blog-post-refresh-hevo',
  '/ui/agent/content-repurposing',
  '/ui/agent/answer-engine-optimization-demo',

  // Non-screen agents
  '/ui/agent/aeo',
  '/ui/agent/seo-audit/*',
  '/ui/agent/blog-post-content-strategizer/*',
  '/ui/agent/blog-post-content-refresher/*',
  '/ui/agent/negative-keyword-analyzer/*',
] as const;

/**
 * Check if a given route path should allow content overflow/scrolling
 *
 * @param pathname - The current route pathname (e.g., from useLocation())
 * @returns true if the route should be scrollable, false otherwise
 *
 * @example
 * ```ts
 * const location = useLocation();
 * const isScrollable = isScrollableRoute(location.pathname);
 * ```
 */
export function isScrollableRoute(pathname: string): boolean {
  return SCROLLABLE_ROUTES.some(route => Boolean(matchPath(route, pathname)));
}
